import React from 'react';
import {
  Settings,
  Link2,
  Package,
  FileText,
  TrendingUp,
  ArrowRight,
  Clock,
  Zap,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';
import './RecommendationCard.css';

/**
 * RecommendationCard - Displays a prioritized recommendation
 *
 * Types:
 * - CONFIGURE_EXISTING: Products adopted but not fully configured
 * - INTEGRATE_EXISTING: Missing integrations between adopted products
 * - MISSING_TECHNOLOGY: No product for the function
 * - GOVERNANCE_ACTION: Policy/process gaps
 *
 * Props:
 * - type: 'CONFIGURE_EXISTING' | 'INTEGRATE_EXISTING' | 'MISSING_TECHNOLOGY' | 'GOVERNANCE_ACTION'
 * - priority: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW'
 * - title: string
 * - description: string
 * - maturityImpact: number (score improvement, e.g., +0.8)
 * - effort: 'LOW' | 'MEDIUM' | 'HIGH'
 * - product: string | null
 * - functionCode: string
 * - functionName: string
 * - currentLevel: string
 * - targetLevel: string
 * - actionLabel: string
 * - onAction: () => void
 * - isCompleted: boolean
 * - compact: boolean
 */
function RecommendationCard({
  type = 'CONFIGURE_EXISTING',
  priority = 'MEDIUM',
  title,
  description,
  maturityImpact = 0,
  effort = 'MEDIUM',
  product,
  functionCode,
  functionName,
  currentLevel,
  targetLevel,
  actionLabel = 'Take Action',
  onAction,
  isCompleted = false,
  compact = false
}) {
  const typeConfig = {
    CONFIGURE_EXISTING: {
      icon: Settings,
      label: 'Configure Existing',
      color: '#8b5cf6',
      bgColor: 'rgba(139, 92, 246, 0.1)'
    },
    INTEGRATE_EXISTING: {
      icon: Link2,
      label: 'Integrate Tools',
      color: '#3b82f6',
      bgColor: 'rgba(59, 130, 246, 0.1)'
    },
    MISSING_TECHNOLOGY: {
      icon: Package,
      label: 'Missing Technology',
      color: '#f59e0b',
      bgColor: 'rgba(245, 158, 11, 0.1)'
    },
    GOVERNANCE_ACTION: {
      icon: FileText,
      label: 'Governance',
      color: '#10b981',
      bgColor: 'rgba(16, 185, 129, 0.1)'
    }
  };

  const priorityConfig = {
    CRITICAL: { color: '#ef4444', label: 'Critical' },
    HIGH: { color: '#f59e0b', label: 'High' },
    MEDIUM: { color: '#3b82f6', label: 'Medium' },
    LOW: { color: '#6b7280', label: 'Low' }
  };

  const effortConfig = {
    LOW: { icon: Zap, label: 'Quick Win', color: '#10b981' },
    MEDIUM: { icon: Clock, label: 'Moderate', color: '#f59e0b' },
    HIGH: { icon: AlertTriangle, label: 'Significant', color: '#ef4444' }
  };

  const config = typeConfig[type] || typeConfig.CONFIGURE_EXISTING;
  const prioConfig = priorityConfig[priority] || priorityConfig.MEDIUM;
  const effConfig = effortConfig[effort] || effortConfig.MEDIUM;

  const TypeIcon = config.icon;
  const EffortIcon = effConfig.icon;

  if (compact) {
    return (
      <div
        className={`rc-compact ${isCompleted ? 'rc-completed' : ''}`}
        style={{ '--rc-color': config.color }}
      >
        <div className="rc-compact-left">
          <div className="rc-compact-icon" style={{ background: config.bgColor, color: config.color }}>
            {isCompleted ? <CheckCircle size={14} /> : <TypeIcon size={14} />}
          </div>
          <div className="rc-compact-content">
            <span className="rc-compact-title">{title}</span>
            {product && <span className="rc-compact-product">{product}</span>}
          </div>
        </div>
        <div className="rc-compact-right">
          {maturityImpact > 0 && (
            <span className="rc-compact-impact" style={{ color: '#10b981' }}>
              <TrendingUp size={12} />
              +{maturityImpact.toFixed(1)}
            </span>
          )}
          <button
            className="rc-compact-action"
            onClick={onAction}
            disabled={isCompleted}
          >
            <ArrowRight size={14} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`rc-card ${isCompleted ? 'rc-completed' : ''}`}
      style={{ '--rc-color': config.color }}
    >
      {/* Header */}
      <div className="rc-header">
        <div className="rc-header-left">
          <div className="rc-type-badge" style={{ background: config.bgColor, color: config.color }}>
            <TypeIcon size={14} />
            <span>{config.label}</span>
          </div>
          <span
            className="rc-priority-badge"
            style={{ background: `${prioConfig.color}20`, color: prioConfig.color }}
          >
            {prioConfig.label}
          </span>
        </div>
        {isCompleted && (
          <div className="rc-completed-badge">
            <CheckCircle size={14} />
            <span>Completed</span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="rc-content">
        <h4 className="rc-title">{title}</h4>
        <p className="rc-description">{description}</p>
      </div>

      {/* Context row */}
      <div className="rc-context">
        {functionName && (
          <div className="rc-context-item">
            <span className="rc-context-label">Function:</span>
            <span className="rc-context-value">{functionName}</span>
          </div>
        )}
        {product && (
          <div className="rc-context-item">
            <span className="rc-context-label">Product:</span>
            <span className="rc-context-value">{product}</span>
          </div>
        )}
        {currentLevel && targetLevel && (
          <div className="rc-context-item">
            <span className="rc-context-label">Level:</span>
            <span className="rc-context-value">
              {currentLevel} → {targetLevel}
            </span>
          </div>
        )}
      </div>

      {/* Metrics row */}
      <div className="rc-metrics">
        {maturityImpact > 0 && (
          <div className="rc-metric rc-metric-impact">
            <TrendingUp size={14} />
            <span className="rc-metric-value">+{maturityImpact.toFixed(1)}</span>
            <span className="rc-metric-label">Impact</span>
          </div>
        )}
        <div className="rc-metric">
          <EffortIcon size={14} style={{ color: effConfig.color }} />
          <span className="rc-metric-value" style={{ color: effConfig.color }}>
            {effConfig.label}
          </span>
          <span className="rc-metric-label">Effort</span>
        </div>
      </div>

      {/* Action */}
      {onAction && (
        <button
          className="rc-action-button"
          onClick={onAction}
          disabled={isCompleted}
        >
          <span>{actionLabel}</span>
          <ArrowRight size={14} />
        </button>
      )}
    </div>
  );
}

export default RecommendationCard;
